import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  FunnelChart, Funnel, LabelList, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { Loader2, ArrowLeft, RefreshCw, Users, Target, FileSearch, Gauge, Bot, MessageSquare, TrendingUp, HelpCircle, BarChart3 } from "lucide-react";
import { BrandLogo } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import AskAnalytics from "@/components/AskAnalytics";
import WeeklyReportCard from "@/components/WeeklyReportCard";
import { adminAnalytics } from "@/lib/api";

const BRAND = "#2563EB";
const PALETTE = ["#2563EB", "#7C3AED", "#0EA5E9", "#F59E0B", "#10B981", "#EF4444"];
const RANGES = [7, 30, 90];
const nf = (n) => (n ?? 0).toLocaleString();

export default function AdminAnalytics() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [rng, setRng] = useState(30);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user === null || (user && user.role !== "admin")) navigate("/login"); }, [user, navigate]);

  const load = async (r = rng) => {
    setLoading(true);
    try { setData(await adminAnalytics(r)); } catch {} finally { setLoading(false); }
  };
  useEffect(() => { load(rng); /* eslint-disable-next-line */ }, [rng]);

  if (user === undefined || user === null || user.role !== "admin")
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-brand" size={26} /></div>;

  const t = data?.totals || {};
  const funnel = data?.funnel || { stages: [], conversion_rate: 0, lost: 0 };
  const funnelData = funnel.stages.map((s, i) => ({ name: s.stage, value: s.value, fill: PALETTE[i % PALETTE.length] }));

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden">
      <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between gap-3 flex-wrap sticky top-0 z-30">
        <Link to="/"><BrandLogo size={34} /></Link>
        <div className="flex items-center gap-4">
          <Link to="/admin" className="text-sm text-slate-500 hover:text-brand">Console</Link>
          <span className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><BarChart3 size={15} /> Analytics</span>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <Link to="/admin" className="text-slate-500 hover:text-brand text-sm flex items-center gap-1.5 mb-2"><ArrowLeft size={14} /> Back to console</Link>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Growth Analytics</h1>
            <p className="text-slate-500 mt-1 text-sm">Real funnel, audit & AI activity — with projected traffic while GA/Search Console connect.</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex bg-white border border-slate-200 rounded-lg p-1" data-testid="range-toggle">
              {RANGES.map((r) => (
                <button key={r} onClick={() => setRng(r)} data-testid={`range-${r}`}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${rng === r ? "bg-brand text-white" : "text-slate-500 hover:text-brand"}`}>{r}d</button>
              ))}
            </div>
            <button onClick={() => load()} className="btn-outline rounded-lg px-3 py-2 flex items-center gap-2 text-sm" data-testid="analytics-refresh">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {data === null ? (
          <div className="p-20 flex justify-center"><Loader2 className="animate-spin text-brand" size={28} /></div>
        ) : (
          <>
            {/* Ask your analytics (natural language) */}
            <AskAnalytics rng={rng} />
            <WeeklyReportCard />

            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
              <Kpi icon={Users} label="Total Leads" value={nf(t.leads)} />
              <Kpi icon={Target} label="Win Rate" value={`${funnel.conversion_rate}%`} accent="#10B981" />
              <Kpi icon={FileSearch} label="Audits Run" value={nf(t.audits)} />
              <Kpi icon={Gauge} label="Avg Score" value={data.audit_score.avg} accent="#F59E0B" />
              <Kpi icon={Bot} label="AI Chats" value={nf(t.ai_conversations)} accent="#7C3AED" />
              <Kpi icon={MessageSquare} label="AI Leads" value={nf(t.ai_leads)} accent="#0EA5E9" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              {/* Funnel */}
              <Panel title="Conversion Funnel" subtitle={`${funnel.total} leads · ${funnel.lost} lost`} testid="funnel-panel">
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <FunnelChart>
                      <Tooltip />
                      <Funnel dataKey="value" data={funnelData} isAnimationActive>
                        <LabelList position="right" fill="#334155" stroke="none" dataKey="name" className="text-xs" />
                        <LabelList position="left" fill="#94a3b8" stroke="none" dataKey="value" className="text-xs" />
                      </Funnel>
                    </FunnelChart>
                  </ResponsiveContainer>
                </div>
              </Panel>

              {/* Leads & audits trend */}
              <Panel title="Leads & Audits Trend" subtitle="Daily activity (real)" className="lg:col-span-2" testid="trend-panel">
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.trend} margin={{ left: -18, right: 8, top: 8 }}>
                      <defs>
                        <linearGradient id="gLeads" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={BRAND} stopOpacity={0.35} /><stop offset="95%" stopColor={BRAND} stopOpacity={0} /></linearGradient>
                        <linearGradient id="gAudits" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#7C3AED" stopOpacity={0.3} /><stop offset="95%" stopColor="#7C3AED" stopOpacity={0} /></linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} interval="preserveStartEnd" minTickGap={24} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} allowDecimals={false} axisLine={false} tickLine={false} width={34} />
                      <Tooltip />
                      <Area type="monotone" dataKey="leads" stroke={BRAND} strokeWidth={2} fill="url(#gLeads)" name="Leads" />
                      <Area type="monotone" dataKey="audits" stroke="#7C3AED" strokeWidth={2} fill="url(#gAudits)" name="Audits" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </Panel>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              {/* Source pie */}
              <Panel title="Leads by Source" subtitle="Where leads come from" testid="source-panel">
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={data.leads_by_source} dataKey="value" nameKey="source" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}>
                        {data.leads_by_source.map((e, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap gap-3 justify-center">
                  {data.leads_by_source.map((e, i) => (
                    <span key={i} className="flex items-center gap-1.5 text-xs text-slate-500"><span className="w-2.5 h-2.5 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} /> {e.source} ({e.value})</span>
                  ))}
                  {data.leads_by_source.length === 0 && <span className="text-xs text-slate-400">No leads yet.</span>}
                </div>
              </Panel>

              {/* Audit score distribution */}
              <Panel title="WebsiteTech Score Spread" subtitle={`${data.audit_score.count} audits · avg ${data.audit_score.avg}`} testid="score-panel">
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.audit_score.distribution} margin={{ left: -10, right: 8, top: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                      <XAxis dataKey="bucket" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} allowDecimals={false} axisLine={false} tickLine={false} width={38} />
                      <Tooltip cursor={{ fill: "#f1f5f9" }} />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                        {data.audit_score.distribution.map((e, i) => <Cell key={i} fill={["#EF4444", "#F59E0B", "#0EA5E9", "#10B981"][i]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Panel>

              {/* Demo traffic */}
              <Panel title="Site Traffic" subtitle="Projected" badge="DEMO" testid="traffic-panel">
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <MiniStat label="Visitors" value={nf(data.demo_traffic.visitors)} />
                  <MiniStat label="Pageviews" value={nf(data.demo_traffic.pageviews)} />
                </div>
                <div className="h-36">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.demo_traffic.series} margin={{ left: -30, right: 4, top: 4 }}>
                      <defs><linearGradient id="gTraffic" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.3} /><stop offset="95%" stopColor="#0EA5E9" stopOpacity={0} /></linearGradient></defs>
                      <XAxis dataKey="date" hide />
                      <YAxis hide domain={["dataMin - 40", "dataMax + 40"]} />
                      <Tooltip />
                      <Area type="monotone" dataKey="visitors" stroke="#0EA5E9" strokeWidth={2} fill="url(#gTraffic)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-[10px] text-slate-400 mt-1">Connect Google Analytics / Search Console for live numbers.</p>
              </Panel>
            </div>

            {/* Sample AI Agent offers */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp size={16} className="text-brand" />
                <h2 className="font-heading text-lg font-bold text-slate-900">Sample AI Agent Offers</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.agent_offers.map((a) => (
                  <div key={a.public_key} data-testid="agent-offer-card" className="bg-white border border-slate-200 rounded-2xl p-5 hover:border-brand/40 transition-colors">
                    <div className="flex items-center justify-between mb-3">
                      <span className="w-10 h-10 rounded-xl flex items-center justify-center text-white" style={{ background: a.primary_color || BRAND }}><Bot size={18} /></span>
                      <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${a.status === "active" ? "bg-green-50 text-green-600" : "bg-slate-100 text-slate-400"}`}>{a.status}</span>
                    </div>
                    <p className="font-heading font-bold text-slate-900">{a.display_name || a.name}</p>
                    <p className="text-xs text-slate-400">{a.site} · {a.industry}</p>
                    <div className="flex gap-4 mt-3 pt-3 border-t border-slate-100 text-xs">
                      <span className="text-slate-500"><b className="text-slate-800">{a.conversation_count}</b> chats</span>
                      <span className="text-slate-500"><b className="text-slate-800">{a.knowledge_count}</b> KB chunks</span>
                    </div>
                    <a href={`/assistant/${a.public_key}`} target="_blank" rel="noopener noreferrer" className="text-xs text-brand font-semibold mt-3 inline-block hover:underline">Preview assistant →</a>
                  </div>
                ))}
              </div>
            </div>

            {/* Knowledge gaps */}
            {data.ai.gaps?.length > 0 && (
              <Panel title="Questions the AI Couldn't Answer" subtitle="Content opportunities" testid="gaps-panel">
                <div className="space-y-1.5">
                  {data.ai.gaps.map((g, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-slate-600 truncate flex items-center gap-2"><HelpCircle size={13} className="text-slate-400 shrink-0" /> {g.question}</span>
                      <span className="text-slate-400 shrink-0 ml-2 text-xs">×{g.count}</span>
                    </div>
                  ))}
                </div>
              </Panel>
            )}
          </>
        )}
      </main>
    </div>
  );
}

const Kpi = ({ icon: Icon, label, value, accent = BRAND }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <span className="w-9 h-9 rounded-lg flex items-center justify-center mb-2" style={{ background: `${accent}14`, color: accent }}><Icon size={16} /></span>
    <p className="font-heading text-2xl font-extrabold text-slate-900">{value}</p>
    <p className="text-[11px] text-slate-400 font-medium mt-0.5">{label}</p>
  </div>
);

const Panel = ({ title, subtitle, badge, children, className = "", testid }) => (
  <div className={`bg-white border border-slate-200 rounded-2xl p-5 ${className}`} data-testid={testid}>
    <div className="flex items-center justify-between mb-4">
      <div>
        <p className="font-heading font-bold text-slate-900 text-sm">{title}</p>
        {subtitle && <p className="text-[11px] text-slate-400">{subtitle}</p>}
      </div>
      {badge && <span className="text-[9px] font-bold uppercase tracking-wide bg-amber-100 text-amber-700 px-2 py-0.5 rounded">{badge}</span>}
    </div>
    {children}
  </div>
);

const MiniStat = ({ label, value }) => (
  <div className="bg-slate-50 rounded-lg p-2.5">
    <p className="font-heading text-lg font-bold text-slate-900">{value}</p>
    <p className="text-[10px] text-slate-400">{label}</p>
  </div>
);
